/**
 * Shared workspace-module query hook — the two queries the sidebar/rail filter
 * and order modules from:
 *   - the workspace module list (carries each module's `sidebar_settings`
 *     config: the user's enabled set + rail order), and
 *   - the per-module sidebar preferences (pinned / primary-rail promotion).
 *
 * Query keys, queryFns, and `enabled`/`staleTime`/`placeholderData` are kept
 * byte-identical to the inline declarations these consumers hold, so every
 * consumer shares ONE React Query cache entry (cache identity depends on the
 * queryKey, and warming depends on the queryFn fetching the same shape).
 *
 * Consumers:
 *   - `useSidebarModulesData` (the wide sidebar) reads the data through this hook.
 *   - `SidebarRail` still holds its own inline `useQuery` calls with the SAME
 *     keys/options, so it shares this cache entry (kept byte-identical on
 *     purpose; if you change the config here, mirror it in SidebarRail).
 *   - `AuthenticatedAppShell` (app/App.tsx) subscribes purely to gate the
 *     authenticated shell on `modulesReady` — without it the rail's first paint
 *     falls back to DEFAULT_VISIBILITY / DEFAULT_RAIL_ORDER and then visibly
 *     re-filters/re-orders once these land (the login/refresh flicker).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { WorkspaceModule, ModuleSidebarPreference } from '@/lib/api/endpoints/workspaces';

export function useWorkspaceModuleQueries(token: string | null | undefined, workspaceId: string) {
  const modulesQuery = useQuery({
    queryKey: queryKeys.modules.list(workspaceId),
    queryFn: () => api.workspaces.listWorkspaceModules(token ?? null, workspaceId, { includeArchived: false }),
    enabled: Boolean(token && workspaceId),
    staleTime: 30_000,
    placeholderData: (previousData: WorkspaceModule[] | undefined) => previousData,
  });

  const preferencesQuery = useQuery({
    queryKey: queryKeys.modules.sidebarPreferences(workspaceId),
    queryFn: () => api.workspaces.listModuleSidebarPreferences(token ?? null, workspaceId),
    enabled: Boolean(token && workspaceId),
    staleTime: 15_000,
    placeholderData: (previousData: ModuleSidebarPreference[] | undefined) => previousData,
  });

  // "Settled" = the first fetch has produced data OR errored. A query that
  // resolves with an empty array (a brand-new workspace with no sidebar_settings
  // row yet — SidebarRail auto-creates it lazily) counts as ready: we must NOT
  // wait for that row to exist, and an error must RELEASE the gate (render the
  // rail with defaults) rather than hang the shell on the loader forever.
  const modulesReady =
    !token || !workspaceId
      ? true
      : (modulesQuery.data !== undefined || modulesQuery.isError) &&
        (preferencesQuery.data !== undefined || preferencesQuery.isError);

  return { modulesQuery, preferencesQuery, modulesReady };
}
